import { useMemo, useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { formatInteger } from "../formatters";
import {
  BRAND_STAGES,
  BrandStagesBrand,
  StageCounts,
  formatOptionalPercent,
  formatSignedPercent,
  retainedPercent,
  vsPreviousPercent,
} from "./brandStagesApiMapping";
import BrandStagesTooltipContent, { BRAND_STAGES_TOOLTIP_SLOT_PROPS } from "./BrandStagesTooltipContent";
import BrandStagesMatrixSkeleton from "./BrandStagesMatrixSkeleton";
import BrandStagesErrorBanner from "./BrandStagesErrorBanner";

type BarScale = "absolute" | "percentOfPaid";

// Bars take at most 62% of a cell, leaving room for the numbers.
const MAX_BAR_PERCENT = 62;
const MIN_BAR_PERCENT = 1.5;

export interface BrandStagesMatrixColumn {
  brand: BrandStagesBrand;
  counts: StageCounts;
}

interface BrandStagesMatrixProps {
  // Brands whose Lead Funnel loaded, already sorted by paid count desc.
  columns: BrandStagesMatrixColumn[];
  // Brand names whose Lead Funnel failed, or null if the brand list itself
  // failed to load.
  failedBrandNames: string[] | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  rangeLabel: string;
  onCellClick: (brand: BrandStagesBrand, stageIndex: number) => void;
}

// Stages x brands matrix (spec Section 3.4): read a column for one brand's
// funnel, a row to compare brands at a stage.
export default function BrandStagesMatrix({
  columns,
  failedBrandNames,
  isLoading,
  isError,
  onRetry,
  rangeLabel,
  onCellClick,
}: BrandStagesMatrixProps) {
  const [scale, setScale] = useState<BarScale>("absolute");

  const maxPaid = useMemo(() => Math.max(1, ...columns.map((column) => column.counts[0])), [columns]);
  const gridTemplateColumns = `150px repeat(${Math.max(1, columns.length)}, minmax(0, 1fr))`;

  const barWidthPercent = (count: number, brandPaid: number): number => {
    if (!count) return 0;
    const ratio = scale === "absolute" ? count / maxPaid : brandPaid ? count / brandPaid : 0;
    return Math.max(MIN_BAR_PERCENT, ratio * MAX_BAR_PERCENT);
  };

  const errorMessage =
    failedBrandNames === null
      ? "Couldn't load the brand list for this range."
      : `Couldn't load stage counts for ${failedBrandNames.join(", ")}.`;

  const renderBody = () => {
    if (isLoading) return <BrandStagesMatrixSkeleton />;
    if (failedBrandNames === null) return null;
    if (columns.length === 0) {
      return isError ? null : (
        <Typography sx={{ py: 3, fontSize: 13.5, color: "#9e9e9e" }}>
          No paid leads were reported to any brand in the selected range.
        </Typography>
      );
    }

    return (
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 1000 }}>
          <Box sx={{ display: "grid", gridTemplateColumns }}>
            <Box
              sx={{ p: "8px 10px 12px", fontSize: 12.5, fontWeight: 700, color: "#212121", borderBottom: "1px solid #ececec" }}
            >
              Stage
            </Box>
            {columns.map(({ brand, counts }) => (
              <Box
                key={brand.brandUid}
                sx={{ p: "8px 12px 12px", borderBottom: "1px solid #ececec", borderLeft: "1px solid #f4f4f4" }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>{brand.brandName}</Typography>
                <Typography sx={{ fontSize: 12, color: "#9e9e9e", mt: 0.25 }}>
                  {formatInteger(counts[0])} paid · {formatOptionalPercent(retainedPercent(counts, 4))} funded
                </Typography>
              </Box>
            ))}
          </Box>

          {BRAND_STAGES.map((stage, stageIndex) => (
            <Box key={stage.step} sx={{ display: "grid", gridTemplateColumns }}>
              <Box sx={{ p: "14px 10px", display: "flex", alignItems: "center", gap: "9px", borderBottom: "1px solid #f4f4f4" }}>
                <Box
                  component="span"
                  sx={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, backgroundColor: stage.color }}
                />
                <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: "#212121" }}>{stage.name}</Typography>
              </Box>

              {columns.map(({ brand, counts }) => {
                const count = counts[stageIndex];
                const change = vsPreviousPercent(counts, stageIndex);
                const changeLabel =
                  stageIndex === 0 ? "entry" : change === null ? "—" : `${formatSignedPercent(change)} vs prev.`;
                const changeColor = change === null ? "#bdbdbd" : change < 0 ? "#C62828" : "#2E7D32";

                return (
                  <Tooltip
                    key={brand.brandUid}
                    placement="top"
                    enterDelay={0}
                    leaveDelay={0}
                    disableInteractive
                    slotProps={BRAND_STAGES_TOOLTIP_SLOT_PROPS}
                    title={
                      <BrandStagesTooltipContent
                        stageIndex={stageIndex}
                        counts={counts}
                        scopeLabel={brand.brandName}
                        rangeLabel={rangeLabel}
                      />
                    }
                  >
                    <Box
                      onClick={() => onCellClick(brand, stageIndex)}
                      data-tracker="1156"
                      sx={{
                        p: "11px 12px",
                        cursor: "pointer",
                        borderLeft: "1px solid #f4f4f4",
                        borderBottom: "1px solid #f4f4f4",
                        "&:hover": { backgroundColor: "#faf5fc" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, height: 22 }}>
                        <Box
                          sx={{
                            height: 22,
                            borderRadius: "4px",
                            backgroundColor: stage.color,
                            width: `${barWidthPercent(count, counts[0])}%`,
                            flexShrink: 0,
                            transition: "width .25s ease",
                          }}
                        />
                        <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", whiteSpace: "nowrap" }}>
                          {formatInteger(count)}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: 11.5, color: "#757575", whiteSpace: "nowrap" }}>
                          {stageIndex === 0 ? "" : `${formatOptionalPercent(retainedPercent(counts, stageIndex))} retained`}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, mt: "5px", fontWeight: 500, color: changeColor }}>
                        {changeLabel}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4.25, pt: 3.25, borderTop: "1px solid #ececec" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 0.25, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#1a1a1a" }}>Stages by Brand</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scale}
          onChange={(_event, next: BarScale | null) => next && setScale(next)}
          sx={{
            ml: "auto",
            backgroundColor: "#f0f0f0",
            borderRadius: "6px",
            p: "3px",
            gap: "2px",
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "4px !important",
              textTransform: "none",
              fontSize: 12.5,
              fontWeight: 500,
              letterSpacing: ".3px",
              px: 1.5,
              py: 0.75,
              color: "#616161",
            },
            "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
              backgroundColor: "#fff",
              color: "#212121",
              boxShadow: "0 1px 3px rgba(0,0,0,.14)",
            },
          }}
        >
          <ToggleButton value="absolute" data-tracker="1158">
            Bars: absolute
          </ToggleButton>
          <ToggleButton value="percentOfPaid" data-tracker="1159">
            Bars: % of paid
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e" }}>
        Read a column top to bottom for one brand's funnel; read across a row to compare brands at a stage. Click any
        cell to open its leads.
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mt: 0.5, mb: 1.75 }}>
        {scale === "absolute"
          ? "Bars share one scale across brands — compare volume."
          : "Bars are relative to each brand's own paid count — compare conversion."}
      </Typography>
      {isError && !isLoading && <BrandStagesErrorBanner message={errorMessage} onRetry={onRetry} />}
      {renderBody()}
    </Box>
  );
}
